import { FunctionComponent, useState, useRef, useEffect } from "react";
import TextOptionBox from "./TextOptionBox";
import Button from "../Button/Button";
import { AnswerChoice, FormData, toFormData } from "../form-data";
import { ComponentId, PageId } from "../../../lib/types/ids";
import { ComponentTextOption, PageTextOption } from "../../../lib/ssi/form.types";
import { Score } from "../../../lib/types/form/submission/score";
import { ValidateResultInline } from "../../../lib/validations/validate";

type TextOptionBoxesProps = {
  data: PageTextOption;
  onDataSelected: (value: FormData) => void;
  onBack?: () => void;
  resolveExpression: (expression: string) => unknown;
  errors: { [key: string]: ValidateResultInline };
  isActive: boolean;
  nextPage?: PageId;
  isLoading?: boolean;
};

interface SelectProps {
  id: ComponentId;
  label: string;
  value?: Score;
}

const TextOptionBoxes: FunctionComponent<TextOptionBoxesProps> = ({
  data,
  onDataSelected,
  onBack,
  resolveExpression,
  isActive,
  errors,
  nextPage,
  isLoading,
}) => {
  const [choices, setChoices] = useState<Array<SelectProps>>([]);
  const [choicesArray, setChoicesArray] = useState<Array<string>>([]);

  const ref = useRef(choices);
  useEffect(() => {
    ref.current = choices;
  }, [choices]);

  useEffect(() => {
    if (isActive) {
      document.addEventListener("keydown", onContinue);
      return () => {
        document.removeEventListener("keydown", onContinue);
      };
    }
    return;
  }, [isActive]);

  const onContinue = (e: KeyboardEvent) => {
    if (e.key === "Enter" || e.keyCode === 13) {
      onSelectData();
    }
  };
  const onBoxSelected = ({ id, label, value }: SelectProps) => {
    const index = choices.findIndex((el) => el.id === id);
    if (data.properties.allowMultipleSelection) {
      if (index === -1) {
        setChoices((current) => [...current, { id, label, value }]);
        setChoicesArray((current) => [...current, id]);
      } else {
        setChoices((current) => [
          ...current.slice(0, index),
          ...current.slice(index + 1),
        ]);
        setChoicesArray((current) => [
          ...current.slice(0, index),
          ...current.slice(index + 1),
        ]);
      }
    } else {
      setChoices([{ id, label, value }]);
      setChoicesArray([id]);
      onSelectData({ id, label, value });
    }
  };

  const content: JSX.Element[] = [];

  const visibleComponents: ComponentTextOption[] = [];
  data.components.forEach((el) => {
    if (el.condition && !resolveExpression(el.condition)) {
      // Entferne eventuell zuvor gegebene Antworten für diese Komponente.
      // z. B. Zubuchungen sind abhängig von Tarifen
      {
        const index = choices.findIndex(c => c.id === el.id);
        if (index > -1) {
          choices.splice(index, 1);
        }
      }
      {
        const index = choicesArray.indexOf(el.id);
        if (index > -1) {
          choicesArray.splice(index, 1);
        }
      }
      return;
    }
  
    visibleComponents.push(el);
  });

  const width = `lg:w-1/${Math.min(4, visibleComponents.length)}`;

  visibleComponents.forEach((el) => {
    (content[el.index] = (
      <div className={`px-2 py-2 lg:py-0 lg:px-4 ${width}`} key={el.id}>
        <TextOptionBox
          {...el}
          onSelect={() =>
            onBoxSelected({ id: el.id, label: el.heading, value: el.value })
          }
          questionId={data.id}
          isSelected={choicesArray.includes(el.id)}
        />
      </div>
    ))
  });
  const onSelectData = (customAnswer?: AnswerChoice) => {
    onDataSelected(
      toFormData("multiple_choice", data, ref.current, customAnswer)
    );
  };
  return (
    <div>
      <div className="flex flex-wrap items-stretch mb-12 -mx-2 lg:-mx-4">
        {content}
      </div>
      {errors?.general ? (
        <p className="text-center mb-4 -mt-6 text-red-500">{errors.general.text}</p>
      ) : null}
      <div className="flex flex-wrap justify-center gap-10">
        {onBack ? (
          <Button variant="outline" onPress={() => onBack()}>
            Zurück
          </Button>
        ) : null}
        {data.properties.allowMultipleSelection ? (
          <Button
            variant="primary"
            onPress={() => onSelectData()}
            isLoading={isLoading}
          >
            {nextPage ? "Weiter" : "Anmelden"}
          </Button>
        ) : null}
      </div>
    </div>
  );
};

export default TextOptionBoxes;
